import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingIndicator } from '../components/LoadingIndicator';

interface ThinkingMessage {
  message: string;
  minDuration: number; // minimum time to show this message in ms
  maxDuration: number; // maximum time to show this message in ms
}

// Messages showing the logical progression of the AI's thinking process
const thinkingStages: ThinkingMessage[] = [
  {
    message: t('Understanding your question...', { ns: 'app' }),
    minDuration: 2000,
    maxDuration: 4000,
  },
  {
    message: t('Analyzing context...', { ns: 'app' }),
    minDuration: 3000,
    maxDuration: 6000,
  },
  {
    message: t('Searching for relevant information...', { ns: 'app' }),
    minDuration: 5000,
    maxDuration: 10000,
  },
  {
    message: t('Running queries...', { ns: 'app' }),
    minDuration: 4000,
    maxDuration: 12000,
  },
  {
    message: t('Examining data...', { ns: 'app' }),
    minDuration: 6000,
    maxDuration: 15000,
  },
  {
    message: t('Processing results...', { ns: 'app' }),
    minDuration: 3000,
    maxDuration: 8000,
  },
  {
    message: t('Analyzing results...', { ns: 'app' }),
    minDuration: 5000,
    maxDuration: 10000,
  },
  {
    message: t('Double-checking findings...', { ns: 'app' }),
    minDuration: 4000,
    maxDuration: 9000,
  },
  {
    message: t('Still working...', { ns: 'app' }),
    minDuration: 7000,
    maxDuration: 15000,
  },
  {
    message: t('Almost done...', { ns: 'app' }),
    minDuration: 5000,
    maxDuration: 10000,
  },
  {
    message: t('Finalizing response...', { ns: 'app' }),
    minDuration: 3000,
    maxDuration: 7000,
  },
];

// Additional messages that can be randomly inserted after longer waits
const extendedThinkingMessages: ThinkingMessage[] = [
  {
    message: t('Performing deep analysis...', { ns: 'app' }),
    minDuration: 8000,
    maxDuration: 15000,
  },
  {
    message: t('Analyzing data...', { ns: 'app' }),
    minDuration: 7000,
    maxDuration: 14000,
  },
  {
    message: t('Working through how to display the results...', { ns: 'app' }),
    minDuration: 8000,
    maxDuration: 16000,
  },
  {
    message: t('Running alternative logic...', { ns: 'app' }),
    minDuration: 6000,
    maxDuration: 12000,
  },
  {
    message: t('Optimizing results...', { ns: 'app' }),
    minDuration: 5000,
    maxDuration: 10000,
  },
  {
    message: t('Running additional checks...', { ns: 'app' }),
    minDuration: 4000,
    maxDuration: 9000,
  },
  {
    message: t('Organizing findings...', { ns: 'app' }),
    minDuration: 5000,
    maxDuration: 11000,
  },
  {
    message: t('Still crunching the numbers...', { ns: 'app' }),
    minDuration: 7000,
    maxDuration: 13000,
  },
];

const AiAgentThinking: React.FC = () => {
  const { t } = useTranslation('app');
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(
    thinkingStages[0].message,
  );
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const startTime = Date.now();

    // Move this function outside updateMessage so it's available throughout useEffect
    const getRandomDuration = (min: number, max: number) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    // Function to progress through thinking stages
    const updateMessage = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      setElapsedTime(elapsed);

      // Determine next message
      if (messageIndex < thinkingStages.length - 1) {
        // Regular progression through stages
        setMessageIndex(prevIndex => prevIndex + 1);
        setCurrentMessage(thinkingStages[messageIndex + 1].message);
      } else {
        // After going through all stages, alternate between the last few regular
        // messages and extended messages, with preference for extended ones as time goes on
        const useExtendedMessage = Math.random() < 0.7; // 70% chance after all regular messages

        if (useExtendedMessage && extendedThinkingMessages.length > 0) {
          // Pick random extended message
          const randomIndex = Math.floor(
            Math.random() * extendedThinkingMessages.length,
          );
          setCurrentMessage(extendedThinkingMessages[randomIndex].message);
        } else {
          // Cycle through the last 3 regular messages
          const lastMessages = thinkingStages.slice(-3);
          const randomIndex = Math.floor(Math.random() * lastMessages.length);
          setCurrentMessage(lastMessages[randomIndex].message);
        }
      }
    };

    // Set initial message
    setCurrentMessage(thinkingStages[0].message);

    // Random duration for first message
    const initialDuration = getRandomDuration(
      thinkingStages[0].minDuration,
      thinkingStages[0].maxDuration,
    );

    // Set up timer for message transitions
    const timer = setTimeout(updateMessage, initialDuration);

    // Set up recurring updates
    const interval = setInterval(
      () => {
        const currentStage =
          messageIndex < thinkingStages.length
            ? thinkingStages[messageIndex]
            : extendedThinkingMessages[
                messageIndex % extendedThinkingMessages.length
              ];

        const nextDuration = getRandomDuration(
          currentStage.minDuration,
          currentStage.maxDuration,
        );

        updateMessage();
      },
      getRandomDuration(4000, 8000),
    ); // Changes every 4-8 seconds after initial progression

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  // Add elapsed time display for very long waits (over 30 seconds)
  const getElapsedTimeDisplay = () => {
    if (elapsedTime < 30000) return null;
    const seconds = Math.floor(elapsedTime / 1000);
    if (seconds < 60) return t('{{seconds}}s', { seconds });

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return t('{{minutes}}m {{remainingSeconds}}s', {
      minutes,
      remainingSeconds,
    });
  };

  return (
    <div className="flex items-center gap-2">
      <LoadingIndicator
        inline
        transparent
        className="text-menu-text/60"
        message={currentMessage}
      />
      {getElapsedTimeDisplay() && (
        <div className="text-menu-text/40">{getElapsedTimeDisplay()}</div>
      )}
    </div>
  );
};

export default AiAgentThinking;
